import { c as _c } from "react-compiler-runtime";
import { announce } from "@react-aria/live-announcer";
import { useControlledState } from "@react-stately/utils";
import * as React from "react";
import { Autocomplete, Button as AriaButton, Group, OverlayTriggerStateContext, Provider, Select as AriaSelect, SelectStateContext, SelectValue as AriaSelectValue } from "react-aria-components";
import { ChevronDown } from "../icons/line.js";
import { Button } from "./Button.js";
import * as styles from "./ContentSelect.css.js";
import { ListBox } from "./ListBox.js";
import { Popover } from "./Popover.js";
import { SearchInput } from "./SearchInput.js";
import { Spinner } from "./Spinner.js";
import useI18n from "./internal/useI18n.js";
import { useSuccessSpinnerState } from "./internal/useSuccessSpinnerState.js";
import { AvatarContext } from "./shared/AvatarContext.js";

// We'll handle labels ourselves.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function ConfirmAction(t0) {
  const $ = _c(17);
  const {
    cancelText,
    confirmText,
    onConfirm
  } = t0;
  const i18n = useI18n();
  const state = React.useContext(OverlayTriggerStateContext);
  let t1;
  if ($[0] !== state) {
    t1 = () => {
      state?.close();
    };
    $[0] = state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cancelText || $[3] !== i18n) {
    t2 = cancelText ?? i18n.format("confirmActionCancel");
    $[2] = cancelText;
    $[3] = i18n;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = /*#__PURE__*/_jsx("div", {
      className: styles.confirmActionButton,
      children: /*#__PURE__*/_jsx(Button, {
        size: "xs",
        hierarchy: "transparent",
        onPress: t1,
        children: t2
      })
    });
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== confirmText || $[9] !== i18n) {
    t4 = confirmText ?? i18n.format("confirmActionConfirm");
    $[8] = confirmText;
    $[9] = i18n;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== onConfirm || $[12] !== t4) {
    t5 = /*#__PURE__*/_jsx("div", {
      className: styles.confirmActionButton,
      children: /*#__PURE__*/_jsx(Button, {
        size: "xs",
        hierarchy: "primary",
        onPress: onConfirm,
        children: t4
      })
    });
    $[11] = onConfirm;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = /*#__PURE__*/_jsxs(Group, {
      className: styles.confirmActionWrapper,
      children: [t3, t5]
    });
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function SelectValue(t0) {
  const $ = _c(11);
  let overrideId;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      overrideId,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = overrideId;
    $[2] = props;
    $[3] = size;
  } else {
    overrideId = $[1];
    props = $[2];
    size = $[3];
  }
  const state = React.useContext(SelectStateContext);
  if (state == null) {
    throw new Error("SelectValue must be used within a ContentSelect component.");
  }
  let t1;
  if ($[4] !== overrideId || $[5] !== state) {
    t1 = overrideId == null ? null : state.collection.getItem(overrideId);
    $[4] = overrideId;
    $[5] = state;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const override = t1;
  let t2;
  if ($[7] !== override?.textValue || $[8] !== props || $[9] !== size) {
    t2 = /*#__PURE__*/_jsx(AriaSelectValue, {
      className: styles.ariaSelectValue,
      children: t3 => {
        const {
          selectedText,
          isPlaceholder
        } = t3;
        return /*#__PURE__*/_jsx("span", {
          title: isPlaceholder ? props.placeholder : selectedText,
          className: styles.selectValue({
            size,
            isPlaceholder
          }),
          children: override?.textValue ?? (isPlaceholder ? props.placeholder : selectedText)
        });
      }
    });
    $[7] = override?.textValue;
    $[8] = props;
    $[9] = size;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// When/if https://github.com/adobe/react-spectrum/pull/8733 gets merged in, we can simplify this component significantly.

export function ContentSelect(t0) {
  const $ = _c(72);
  let ariaLabel;
  let children;
  let confirmActionTextCancel;
  let confirmActionTextConfirm;
  let defaultOpen;
  let defaultValue;
  let isOpenProp;
  let left;
  let loadingState;
  let onChange;
  let onOpenChangeProp;
  let popoverWidth;
  let props;
  let size;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      ariaLabel,
      loadingState,
      popoverWidth,
      size,
      left,
      children,
      confirmAction: t1,
      confirmActionTextCancel,
      confirmActionTextConfirm,
      value,
      defaultValue,
      onChange,
      isOpen: isOpenProp,
      defaultOpen,
      onOpenChange: onOpenChangeProp,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = children;
    $[3] = confirmActionTextCancel;
    $[4] = confirmActionTextConfirm;
    $[5] = defaultOpen;
    $[6] = defaultValue;
    $[7] = isOpenProp;
    $[8] = left;
    $[9] = loadingState;
    $[10] = onChange;
    $[11] = onOpenChangeProp;
    $[12] = popoverWidth;
    $[13] = props;
    $[14] = size;
    $[15] = t1;
    $[16] = value;
  } else {
    ariaLabel = $[1];
    children = $[2];
    confirmActionTextCancel = $[3];
    confirmActionTextConfirm = $[4];
    defaultOpen = $[5];
    defaultValue = $[6];
    isOpenProp = $[7];
    left = $[8];
    loadingState = $[9];
    onChange = $[10];
    onOpenChangeProp = $[11];
    popoverWidth = $[12];
    props = $[13];
    size = $[14];
    t1 = $[15];
    value = $[16];
  }
  const confirmAction = t1 === undefined ? false : t1;
  const i18n = useI18n();
  const triggerRef = React.useRef(null);
  const spinnerState = useSuccessSpinnerState(loadingState);
  const [temporaryKey, setTemporaryKey] = React.useState(null);
  const [key, setKey] = useControlledState(value, defaultValue ?? null, onChange);
  const canClose = React.useRef(true);
  const [isOpen, setOpen] = useControlledState(isOpenProp, defaultOpen ?? false, onOpenChangeProp);
  let t2;
  if ($[17] !== setOpen) {
    t2 = value_0 => {
      if (!canClose.current) {
        canClose.current = true;
        return;
      }
      setTemporaryKey(null);
      setOpen(value_0);
    };
    $[17] = setOpen;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const onOpenChange = t2;
  let t3;
  if ($[19] !== confirmAction || $[20] !== i18n || $[21] !== setKey) {
    t3 = key_0 => {
      if (confirmAction) {
        canClose.current = false;
        setTemporaryKey(key_0);
        announce(i18n.format("confirmActionSelectedAnnouncement"));
      } else {
        canClose.current = true;
        setKey(key_0);
      }
    };
    $[19] = confirmAction;
    $[20] = i18n;
    $[21] = setKey;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const onChangeCallback = t3;
  const t4 = temporaryKey ?? key;
  let t5;
  if ($[23] !== size) {
    t5 = styles.selectButton({
      size
    });
    $[23] = size;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [[AvatarContext, {
      size: "xs",
      isInteractive: false,
      tooltip: false
    }]];
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== left) {
    t7 = typeof left === "function" ? /*#__PURE__*/React.createElement(left, {
      className: styles.leftIcon
    }) : left;
    $[26] = left;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t7) {
    t8 = /*#__PURE__*/_jsx(Provider, {
      values: t6,
      children: t7
    });
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== key || $[31] !== props.placeholder || $[32] !== size) {
    t9 = /*#__PURE__*/_jsx(SelectValue, {
      size: size,
      placeholder: props.placeholder,
      overrideId: key
    });
    $[30] = key;
    $[31] = props.placeholder;
    $[32] = size;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== loadingState || $[35] !== props.isDisabled || $[36] !== spinnerState) {
    t10 = props.isDisabled === true ? null : /*#__PURE__*/_jsx("div", {
      className: styles.chevronIconWrapper,
      role: "presentation",
      children: loadingState == null || spinnerState.renderState === "success" ? /*#__PURE__*/_jsx(ChevronDown, {
        "aria-hidden": true,
        className: styles.chevronIcon
      }) : /*#__PURE__*/_jsx(Spinner, {
        size: "xs",
        ...spinnerState
      })
    });
    $[34] = loadingState;
    $[35] = props.isDisabled;
    $[36] = spinnerState;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t5 || $[40] !== t8 || $[41] !== t9) {
    t11 = /*#__PURE__*/_jsxs(AriaButton, {
      ref: triggerRef,
      className: t5,
      children: [t8, t9, t10]
    });
    $[38] = t10;
    $[39] = t5;
    $[40] = t8;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const t12 = popoverWidth;
  let t13;
  if ($[43] !== ariaLabel || $[44] !== props) {
    t13 = props.onSearch ? /*#__PURE__*/_jsx(SearchInput, {
      ...props,
      ariaLabel: ariaLabel
    }) : null;
    $[43] = ariaLabel;
    $[44] = props;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const t14 = size === "breadcrumbs" ? "m" : size;
  let t15;
  if ($[46] !== children || $[47] !== t14) {
    t15 = /*#__PURE__*/_jsx(ListBox, {
      size: t14,
      escapeKeyBehavior: "none",
      children: children
    });
    $[46] = children;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== confirmAction || $[50] !== confirmActionTextCancel || $[51] !== confirmActionTextConfirm || $[52] !== onOpenChange || $[53] !== setKey || $[54] !== temporaryKey) {
    t16 = confirmAction ? /*#__PURE__*/_jsx(ConfirmAction, {
      confirmText: confirmActionTextConfirm,
      cancelText: confirmActionTextCancel,
      onConfirm: () => {
        setKey(temporaryKey);
        setTemporaryKey(null);
        onOpenChange(false);
      }
    }) : null;
    $[49] = confirmAction;
    $[50] = confirmActionTextCancel;
    $[51] = confirmActionTextConfirm;
    $[52] = onOpenChange;
    $[53] = setKey;
    $[54] = temporaryKey;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== t13 || $[57] !== t15 || $[58] !== t16) {
    t17 = /*#__PURE__*/_jsx(Autocomplete, {
      children: /*#__PURE__*/_jsxs("div", {
        role: "presentation",
        className: styles.popoverWrapper,
        children: [t13, t15, t16]
      })
    });
    $[56] = t13;
    $[57] = t15;
    $[58] = t16;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== t12 || $[61] !== t17) {
    t18 = /*#__PURE__*/_jsx(Popover, {
      triggerRef: triggerRef,
      width: t12,
      placement: "bottom end",
      children: t17
    });
    $[60] = t12;
    $[61] = t17;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== ariaLabel || $[64] !== isOpen || $[65] !== onChangeCallback || $[66] !== onOpenChange || $[67] !== props || $[68] !== t11 || $[69] !== t18 || $[70] !== t4) {
    t19 = /*#__PURE__*/_jsxs(AriaSelect, {
      className: styles.select,
      "aria-label": ariaLabel,
      value: t4,
      isOpen: isOpen,
      onOpenChange: onOpenChange,
      onChange: onChangeCallback,
      ...props,
      children: [t11, t18]
    });
    $[63] = ariaLabel;
    $[64] = isOpen;
    $[65] = onChangeCallback;
    $[66] = onOpenChange;
    $[67] = props;
    $[68] = t11;
    $[69] = t18;
    $[70] = t4;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  return t19;
}
//# sourceMappingURL=ContentSelect.js.map